import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import type { ReactNode } from "react";
import { AuthProvider } from "./lib/auth";
import { BrandingProvider, useBranding } from "./lib/branding";
import { Layout } from "./components/Layout";
import { RequireAuth } from "./components/RequireAuth";
import { HomePage } from "./pages/HomePage";
import { SignInPage } from "./pages/SignInPage";
import { VerifyPage } from "./pages/VerifyPage";
import { KbListPage } from "./pages/KbListPage";
import { KbArticlePage } from "./pages/KbArticlePage";
import { TicketsPage } from "./pages/TicketsPage";
import { TicketDetailPage } from "./pages/TicketDetailPage";
import { NewTicketPage } from "./pages/NewTicketPage";

/** Tenant-managed availability: when the tenant has switched the portal off
 *  (Settings → Portal in the agent app), render a branded closed page instead of
 *  the app. /settings stays readable server-side precisely so this can render. */
function AvailabilityGate({ children }: { children: ReactNode }) {
  const { settings, loading } = useBranding();
  if (!loading && settings.portal.enabled === false) {
    return (
      <div className="container" style={{ maxWidth: 560, margin: "12vh auto", textAlign: "center" }}>
        <h1>We'll be right back</h1>
        <p className="muted">
          {settings.portal.welcome || "This help portal is temporarily unavailable. Please check back soon."}
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      {/* Branding + auth are app-wide: settings (public) drive the accent color and
          welcome copy; the auth probe (GET /tickets) decides signed-in state. */}
      <BrandingProvider>
        <AuthProvider>
          <AvailabilityGate>
          <Routes>
            <Route element={<Layout />}>
              {/* Public */}
              <Route path="/" element={<HomePage />} />
              <Route path="/signin" element={<SignInPage />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/kb" element={<KbListPage />} />
              <Route path="/kb/:slug" element={<KbArticlePage />} />

              {/* Authenticated (cookie-gated) */}
              <Route
                path="/tickets"
                element={
                  <RequireAuth>
                    <TicketsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/tickets/new"
                element={
                  <RequireAuth>
                    <NewTicketPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/tickets/:id"
                element={
                  <RequireAuth>
                    <TicketDetailPage />
                  </RequireAuth>
                }
              />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </AvailabilityGate>
        </AuthProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}
